import { useMemo, useState } from "react";

function ApplicantDataTable({ applications = [], title = "Applicants", emptyText = "No applications found.", renderActions }) {
    const [filters, setFilters] = useState({
        keyword: "",
        job: "",
        qualification: "",
        status: "",
        skill: ""
    });

    const jobs = useMemo(() => unique(applications.map((item) => item.job_title).filter(Boolean)), [applications]);
    const statuses = useMemo(() => unique(applications.map((item) => item.status || "submitted").filter(Boolean)), [applications]);

    const filtered = useMemo(() => {
        return applications.filter((item) => {
            const haystack = [
                item.candidate_name,
                item.candidate_email,
                item.candidate_phone,
                item.job_title,
                item.company_name,
                item.candidate_headline,
                item.candidate_summary,
                item.candidate_skills,
                item.candidate_education,
                item.cover_letter
            ].join(" ").toLowerCase();
            const qualification = String(item.candidate_education || "").toLowerCase();
            const skills = String(item.candidate_skills || "").toLowerCase();
            return (!filters.keyword || haystack.includes(filters.keyword.toLowerCase()))
                && (!filters.job || item.job_title === filters.job)
                && (!filters.status || (item.status || "submitted") === filters.status)
                && (!filters.qualification || qualification.includes(filters.qualification.toLowerCase()))
                && (!filters.skill || skills.includes(filters.skill.toLowerCase()));
        });
    }, [applications, filters]);

    const exportCsv = () => {
        const headers = [
            "Candidate Name",
            "Email",
            "Phone",
            "Job",
            "Company",
            "Status",
            "Applied At",
            "Qualification",
            "Skills",
            "Experience Years",
            "Headline",
            "Summary",
            "Cover Letter",
            "Resume Files",
            "Resume Links"
        ];
        const rows = filtered.map((item) => [
            item.candidate_name,
            item.candidate_email,
            item.candidate_phone,
            item.job_title,
            item.company_name,
            item.status || "submitted",
            formatDate(item.created_at),
            item.candidate_education,
            item.candidate_skills,
            item.candidate_experience_years,
            item.candidate_headline,
            item.candidate_summary,
            item.cover_letter,
            (item.resumes || []).map((resume) => resume.file_name).join(" | "),
            (item.resumes || []).map((resume) => resume.download_url).join(" | ")
        ]);
        downloadCsv(`${title.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "applicants"}.csv`, [headers, ...rows]);
    };

    return (
        <div className="applicant-data-table">
            <div className="row align-items-end m-b20">
                <div className="col-lg-3 col-md-6 m-b10">
                    <label className="form-label">Search</label>
                    <input className="form-control" value={filters.keyword} onChange={(event) => setFilters({ ...filters, keyword: event.target.value })} placeholder="Name, email, phone, job" />
                </div>
                <div className="col-lg-2 col-md-6 m-b10">
                    <label className="form-label">Job</label>
                    <select className="form-control" value={filters.job} onChange={(event) => setFilters({ ...filters, job: event.target.value })}>
                        <option value="">All jobs</option>
                        {jobs.map((job) => <option key={job} value={job}>{job}</option>)}
                    </select>
                </div>
                <div className="col-lg-2 col-md-6 m-b10">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })}>
                        <option value="">All status</option>
                        {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                </div>
                <div className="col-lg-2 col-md-6 m-b10">
                    <label className="form-label">Qualification</label>
                    <input className="form-control" value={filters.qualification} onChange={(event) => setFilters({ ...filters, qualification: event.target.value })} placeholder="B.Tech, MBA" />
                </div>
                <div className="col-lg-2 col-md-6 m-b10">
                    <label className="form-label">Skills</label>
                    <input className="form-control" value={filters.skill} onChange={(event) => setFilters({ ...filters, skill: event.target.value })} placeholder="React, Sales" />
                </div>
                <div className="col-lg-1 col-md-6 m-b10">
                    <button className="site-button w-100" type="button" onClick={exportCsv}>Export</button>
                </div>
            </div>
            <div className="d-flex justify-content-between align-items-center m-b10">
                <strong>{filtered.length} of {applications.length} applications</strong>
                <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setFilters({ keyword: "", job: "", qualification: "", status: "", skill: "" })}>Clear filters</button>
            </div>
            <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                    <thead>
                        <tr>
                            <th>Candidate</th>
                            <th>Contact</th>
                            <th>Job</th>
                            <th>Status</th>
                            <th>Qualification</th>
                            <th>Skills / Experience</th>
                            <th>Resume</th>
                            <th>Applied</th>
                            <th>Cover Letter</th>
                            {renderActions && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={renderActions ? 10 : 9} className="text-center">{emptyText}</td></tr>
                        ) : filtered.map((item) => (
                            <tr key={item.id}>
                                <td><strong>{item.candidate_name}</strong><div className="text-muted">{item.candidate_headline || "No headline"}</div></td>
                                <td><div>{item.candidate_email}</div><div>{item.candidate_phone || "No phone"}</div></td>
                                <td><strong>{item.job_title}</strong><div className="text-muted">{item.company_name || ""}</div></td>
                                <td><span className="badge bg-info text-dark">{item.status || "submitted"}</span></td>
                                <td>{item.candidate_education || "Not added"}</td>
                                <td><div>{item.candidate_skills || "Not added"}</div><div className="text-muted">{item.candidate_experience_years || "Experience not added"}</div></td>
                                <td>
                                    {(item.resumes || []).length === 0 ? "No resume" : (item.resumes || []).map((resume) => (
                                        <div key={resume.id}><a href={resume.download_url} target="_blank" rel="noreferrer">{resume.file_name}</a></div>
                                    ))}
                                </td>
                                <td>{formatDate(item.created_at)}</td>
                                <td className="text-break">{item.cover_letter || ""}</td>
                                {renderActions && <td>{renderActions(item)}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function unique(values) {
    return Array.from(new Set(values)).sort();
}

function formatDate(value) {
    if (!value) return "";
    return new Date(value).toLocaleString();
}

function csvCell(value) {
    return `"${String(value || "").replace(/"/g, '""')}"`;
}

function downloadCsv(fileName, rows) {
    const csv = rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
}

export default ApplicantDataTable;
